import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { listSubscriptions, pauseSubscription, resumeSubscription } from '@/api/ops';
import type { SubscriptionView } from '@/api/opsTypes';
import { useCan } from '@/auth/session';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/ui/DataTable';
import { QueryError } from '@/ui/QueryError';

export function SubscriptionsSection() {
  const can = useCan();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ['maintenance', 'subscriptions', page],
    queryFn: () => listSubscriptions({ page }),
  });

  const change = useMutation({
    mutationFn: (sub: SubscriptionView) =>
      sub.status === 'ACTIVE' ? pauseSubscription(sub.id) : resumeSubscription(sub.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['maintenance'] }),
  });

  const columns: ColumnDef<SubscriptionView, never>[] = [
    { accessorKey: 'host', header: 'Receiver host' },
    { accessorKey: 'owner_scope', header: 'Scope' },
    {
      id: 'events',
      header: 'Events',
      cell: ({ row }) => row.original.event_types.join(', '),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'ACTIVE' ? 'secondary' : 'outline'}>
          {row.original.status}
        </Badge>
      ),
    },
    { accessorKey: 'recent_failures', header: 'Failures (24h)', meta: { numeric: true } },
    { accessorKey: 'pending', header: 'Pending', meta: { numeric: true } },
    { accessorKey: 'dead', header: 'Dead', meta: { numeric: true } },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        can('maintenance.operate') ? (
          <Button
            size="sm"
            variant="outline"
            disabled={change.isPending}
            onClick={() => change.mutate(row.original)}
          >
            {row.original.status === 'ACTIVE' ? 'Pause' : 'Re-enable'}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhook subscriptions</CardTitle>
        <CardDescription>
          Pausing loses nothing queued. Re-enabling also revives one the platform disabled after
          repeated dead deliveries.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {query.isError ? (
          <QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            getRowId={(row) => row.id}
            isLoading={query.isPending}
            empty="No subscriptions."
          />
        )}
        {query.data && query.data.total_pages > 1 && (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= query.data.total_pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
